// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Parâmetros de negócio do mapa
// ============================================================================
//
// REGRA DO PROJETO, SEM EXCEPÇÃO
//   Nenhuma regra de negócio ou legal é fixada em código. É exigência expressa da
//   fonte (secções 4.1, 5.1 e 7) e está no `CLAUDE.md`. Os valores abaixo são
//   **valores de omissão de um protótipo**, não decisões: quando existir ecrã de
//   configuração, vêm dele; quando existir base de dados, vêm dela.
//
//   Estão aqui reunidos, e não espalhados, exactamente para que a passagem a
//   configuração real seja uma alteração num sítio.
// ============================================================================

export interface ConfiguracaoMapa {
  /**
   * Tempo mínimo de rotação, em minutos, abaixo do qual o mapa avisa.
   *
   * ⚠️ **D-073 está EM ABERTO** e este mínimo **não existe na fonte** — é uma
   * configuração nova que a camada visual §4 recomendou propor. Quem responde:
   * operações.
   *
   * **Era 120 minutos, e 120 estava errado como limiar.** Vinha de UM caso
   * observado com folga confortável (viatura 215: chegada 18:00, partida 20:00,
   * em Madrid). Medindo os 276 casos em vez de um:
   *
   * | mínimo | p5 | p10 | p25 | **mediana** | máximo |
   * |---|---|---|---|---|---|
   * | 10 min | 35 min | 80 min | 105 min | **120 min** | 855 min |
   *
   * Ou seja **120 minutos é a MEDIANA da prática**, não um mínimo: como limiar
   * marcaria **41 % das rotações reais** como abaixo do mínimo. É a mesma
   * armadilha do aviso que disparava em 23 % dos casos (F-28) — um aviso que
   * dispara em metade da população ensina a ignorar avisos.
   *
   * 60 minutos fica na banda p5–p10 do que eles fazem e dispara em **8 %**.
   */
  readonly minutosRotacaoMinimo: number;
}

export const CONFIGURACAO_OMISSAO: ConfiguracaoMapa = {
  // ⚠️ Isto é a rede de segurança, não a fonte do valor. O valor a valer é o
  // `rotacao_minima_h` de `parametros_custo.csv`, servido por `/api/configuracao` e
  // lido por `configuracaoDeParametros`. Os dois têm de coincidir, e coincidem.
  minutosRotacaoMinimo: 60,
};

/**
 * Constrói a configuração do mapa a partir do que `/api/configuracao` devolve.
 *
 * ⚠️ **É isto que faz o valor deixar de viver em dois sítios.** O `rotacao_minima_h`
 * está em `parametros_custo.csv`, com a sua origem declarada; o mapa passa a lê-lo de
 * lá em vez de ter uma cópia. Dois sítios para o mesmo número acabam por discordar, e
 * este em particular decide quando o planeador é avisado.
 *
 * ⚠️ Um parâmetro em falta ou ilegível **não** derruba o mapa: cai na omissão. A
 * alternativa — recusar desenhar o plano porque um limiar de aviso não veio — trocava
 * uma degradação por uma indisponibilidade, e a fonte 4.2 é explícita quanto a isso.
 */
export function configuracaoDeParametros(
  parametros: readonly { readonly chave?: string; readonly valor?: string }[],
): ConfiguracaoMapa {
  const horas = Number(parametros.find((p) => p.chave === 'rotacao_minima_h')?.valor);
  return {
    minutosRotacaoMinimo:
      Number.isFinite(horas) && horas > 0
        ? Math.round(horas * 60)
        : CONFIGURACAO_OMISSAO.minutosRotacaoMinimo,
  };
}
